// Schlichte, proportionale SVG-Skizze des Regals.
// Zeigt lichte Innenhöhe als Korpus, Regalböden an ihren berechneten Positionen,
// Fächer dezent schattiert, Markiermaße links (Unterkante je Boden ab Innen-Unterkante).
// Keine Interaktion, nur eine ruhige Visualisierung. Liest direkt das calc.js-Ergebnis.
import React from "react";
import { fmtCm } from "@/lib/calc";

// Layout-Konstanten im SVG-Koordinatensystem (rein virtuell, skaliert per viewBox).
const W = 320;          // Gesamtbreite
const LABEL_W = 78;     // linke Spalte für Maße
const PAD_TOP = 14;     // oberer Rand
const PAD_BOTTOM = 22;  // unterer Rand (für "0 cm")
const BODY_H = 360;     // Höhe des Korpus-Bereichs
const BODY_X = LABEL_W + 10;
const BODY_W = W - BODY_X - 16;

export default function ShelfSketch({ result, rounded }) {
  // Bei Fehlern / fehlendem Ergebnis nichts Irreführendes zeigen.
  if (!result || !result.ok || !isFinite(result.innerHeightMm) || result.innerHeightMm <= 0) {
    return (
      <div
        className="rounded-xl border border-dashed border-border/70 bg-muted/20 p-6 text-center text-xs text-muted-foreground"
        data-testid="shelf-sketch-empty"
      >
        Gib gültige Werte ein, dann erscheint hier die Regal-Skizze.
      </div>
    );
  }

  const { innerHeightMm, shelfThicknessMm, shelfPositions } = result;

  // mm-Höhe in SVG-y umrechnen. y=0 oben, Innen-Unterkante unten.
  const usableH = BODY_H - PAD_TOP - PAD_BOTTOM;
  const scale = usableH / innerHeightMm; // px pro mm
  const yBottom = PAD_TOP + usableH;     // Innen-Unterkante (0 cm)
  const yTop = PAD_TOP;                   // Innen-Oberkante
  const toY = (mm) => yBottom - mm * scale; // Höhe ab Unterkante -> y

  // Bodenstärke in Pixeln, mit sinnvoller Mindesthöhe zur Sichtbarkeit.
  const shelfPx = Math.max(3, shelfThicknessMm * scale);

  // Markierwert je Boden (gerundet oder exakt) als Text.
  const markFor = (p) =>
    rounded && p.roundedBottomPositionMm != null
      ? fmtCm(p.roundedBottomPositionMm)
      : fmtCm(p.bottomPositionMm);

  const svgH = BODY_H;

  return (
    <figure className="m-0" data-testid="shelf-sketch" aria-label="Skizze des Regals mit Bodenpositionen">
      <svg
        viewBox={`0 0 ${W} ${svgH}`}
        width="100%"
        className="max-w-[360px] mx-auto block"
        role="img"
        style={{ height: "auto" }}
      >
        {/* Fächer dezent schattiert. Grenzen: Innen-Unterkante, jede Boden-Unterkante
            (Oberkante des Fachs darunter), Boden-Oberkante (Unterkante des Fachs darüber),
            Innen-Oberkante. Robust direkt aus den berechneten Bodenpositionen abgeleitet. */}
        {(() => {
          // Untere Fachgrenzen (Oberseite des darunterliegenden Bodens) ab 0.
          const lowerBoundsMm = [0, ...shelfPositions.map((p) => p.bottomPositionMm + shelfThicknessMm)];
          // Obere Fachgrenzen (Unterseite des darüberliegenden Bodens) bis Innen-Oberkante.
          const upperBoundsMm = [...shelfPositions.map((p) => p.bottomPositionMm), innerHeightMm];
          const rects = [];
          for (let i = 0; i < lowerBoundsMm.length; i++) {
            const yLow = toY(lowerBoundsMm[i]);
            const yHigh = toY(upperBoundsMm[i]);
            const h = yLow - yHigh;
            if (h <= 0) continue;
            rects.push(
              <rect
                key={`comp-${i}`}
                x={BODY_X}
                y={yHigh}
                width={BODY_W}
                height={h}
                fill="hsl(var(--secondary))"
                opacity={i % 2 === 0 ? 0.55 : 0.3}
              />
            );
          }
          return rects;
        })()}

        {/* Korpus-Rahmen (lichte Innenhöhe) */}
        <rect
          x={BODY_X}
          y={yTop}
          width={BODY_W}
          height={yBottom - yTop}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="2"
          rx="3"
        />

        {/* Innen-Unterkante als Bezugslinie */}
        <line
          x1={LABEL_W - 4}
          y1={yBottom}
          x2={BODY_X + BODY_W}
          y2={yBottom}
          stroke="hsl(var(--border))"
          strokeWidth="1"
        />
        <text x={LABEL_W - 8} y={yBottom + 4} textAnchor="end" className="tabular-nums" fontSize="10" fill="hsl(var(--muted-foreground))">
          0 cm
        </text>

        {/* Regalböden an berechneten Positionen */}
        {shelfPositions.map((p) => {
          const yShelfBottom = toY(p.bottomPositionMm);
          return (
            <g key={`shelf-${p.shelfIndex}`}>
              {/* Boden (dunkler Balken, Bodenstärke sichtbar) */}
              <rect
                x={BODY_X}
                y={yShelfBottom - shelfPx}
                width={BODY_W}
                height={shelfPx}
                fill="hsl(var(--primary))"
                opacity="0.92"
                rx="1"
              />
              {/* Maßlinie zur Unterkante */}
              <line
                x1={LABEL_W - 4}
                y1={yShelfBottom}
                x2={BODY_X}
                y2={yShelfBottom}
                stroke="hsl(var(--accent))"
                strokeWidth="1"
                strokeDasharray="2 2"
              />
              {/* Markiermaß links */}
              <text
                x={LABEL_W - 8}
                y={yShelfBottom + 4}
                textAnchor="end"
                className="tabular-nums"
                fontSize="11"
                fontWeight="600"
                fill="hsl(var(--foreground))"
              >
                {markFor(p)}
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption className="mt-2 text-center text-[11px] text-muted-foreground">
        Maße = Unterkante je Boden, ab Innen-Unterkante. Skizze ist proportional, nicht maßstabsgetreu.
      </figcaption>
    </figure>
  );
}
